import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Edit3, Upload, Sparkles, LayoutTemplate, Undo2, FileText } from 'lucide-react';
import { Button, Select } from '../../../components';
import { qualityService } from '../../../services';
import { Badge, Card, FieldLabel, Notice, inputClass, isoPlusDays, errorMessage, isUploadedPolicy, policyFormatLabel } from './shared';

const MAX_BODY = 150000;

/**
 * The policy editor — a full-width workspace, not a dialog.
 *
 * Writing a policy is a long job, and a cramped modal makes people write less
 * than they mean to. So this takes over the page, puts the document in the
 * middle and the ownership details in a narrow column beside it, and keeps the
 * Save button visible at the top.
 *
 * **Everything saved here is a DRAFT.** A draft is not the policy: it is not
 * shown to staff, it is not what an audit links to, and nothing about it takes
 * effect until somebody publishes it. The sidebar note says so, because the
 * distinction is the whole reason the module is versioned.
 *
 * Two writing aids, both optional and both deliberately limited:
 *   • a TEMPLATE inserts headings and questions — never content. A template
 *     that pre-filled a procedure would be a policy nobody wrote.
 *   • PATCH restructures the manager's own words. It cannot add facts, and
 *     publishing is blocked while its `[Needs your input: …]` prompts remain.
 */
const PolicyEditor = ({ policyId, categories, templates, onClose, onSaved, onManageCategories }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(Boolean(policyId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [tool, setTool] = useState(null);           // 'template' | 'patch' | null
  const [templateKey, setTemplateKey] = useState('');
  const [patch, setPatch] = useState(null);         // { structured, gaps, changes }
  const [patchRunning, setPatchRunning] = useState(false);
  const [patchUndo, setPatchUndo] = useState(null); // the text Patch replaced
  const [version, setVersion] = useState(1);
  const [hasCurrent, setHasCurrent] = useState(false);

  const bodyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    if (!policyId) {
      setForm({
        title: '',
        category: categories[0] || '',
        owner_name: '',
        review_date: isoPlusDays(365),
        format: 'text',
        body: '',
        file: null,
        existingFile: null,
      });
      return () => { cancelled = true; };
    }

    qualityService.getPolicy(policyId)
      .then((response) => {
        if (cancelled) return;
        const policy = response.data;
        // Continue the draft if there is one; otherwise start a revision from
        // the published version, pre-filled with its content.
        const source = policy.draft || policy.current;
        setHasCurrent(Boolean(policy.current));
        setVersion(policy.draft ? policy.draft.version : (policy.current?.version || 0) + 1);
        setForm({
          title: source?.title || policy.title || '',
          category: source?.category || policy.category || categories[0] || '',
          owner_name: source?.owner_name || policy.owner_name || '',
          review_date: source?.review_date || isoPlusDays(365),
          // The editor only distinguishes "written here" from "uploaded". The
          // kind of upload is the API's business, decided from the file.
          format: isUploadedPolicy(source) ? 'upload' : 'text',
          body: source?.body || '',
          file: null,
          existingFile: isUploadedPolicy(source) && source?.file_url
            ? { name: source.file_name, size: source.file_size, label: policyFormatLabel(source) }
            : null,
        });
      })
      .catch((err) => {
        toast.error(errorMessage(err, 'This policy could not be opened.'));
        onClose();
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  const templateText = useMemo(() => {
    const template = templates?.[templateKey];
    if (!template) return '';
    return template.sections
      .map(([heading, question]) => `${heading}\n[Needs your input: ${question}]`)
      .join('\n\n');
  }, [templates, templateKey]);

  if (loading || !form) {
    return <div className="py-20 text-center text-[13px] text-[#64748B] dark:text-gray-400">Loading policy…</div>;
  }

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const insertTemplate = () => {
    if (!templateText) return;
    if (form.body.includes(templateText)) {
      setError('This template is already in your policy. Complete the questions below.');
      return;
    }
    const next = form.body.trim() ? `${form.body}\n\n${templateText}` : templateText;
    if (next.length > MAX_BODY) {
      setError('There is not enough space to insert this template. Shorten the policy first.');
      return;
    }
    // Appended, never replacing: notes already written are the manager's work
    // and must survive reaching for a template halfway through.
    set('body', next);
    setError('');
    setTemplateKey('');
    setTool(null);
    toast.success('Template inserted. Your existing text has been kept.');
    bodyRef.current?.focus();
  };

  const runPatch = async () => {
    if (!form.body.trim()) {
      setError('Write or paste your policy notes first. Patch needs your information to work from.');
      return;
    }
    setPatchRunning(true);
    setError('');
    try {
      const response = await qualityService.runPatch(form.body);
      setPatch({ ...response.data, source: form.body });
    } catch (err) {
      setError(errorMessage(err, 'Patch could not structure this text. Your notes are unchanged.'));
    } finally {
      setPatchRunning(false);
    }
  };

  const applyPatch = () => {
    if (!patch) return;
    // The manager may have kept typing while Patch was thinking. Applying a
    // structure built from text that no longer exists would silently throw
    // away whatever they added.
    if (form.body !== patch.source) {
      setError('Your notes changed after the preview. Run Patch again before applying.');
      return;
    }
    setPatchUndo(form.body);
    set('body', patch.structured);
    setPatch(null);
    setError('');
  };

  const undoPatch = () => {
    if (patchUndo === null) return;
    set('body', patchUndo);
    setPatchUndo(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category,
        owner_name: form.owner_name.trim(),
        review_date: form.review_date,
        format: form.format,
        body: form.body,
        file: form.file,
      };
      const response = policyId
        ? await qualityService.savePolicyDraft(policyId, payload)
        : await qualityService.createPolicy(payload);

      toast.success(response.message);
      onSaved(response.data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-[1240px]">
      <header className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <button type="button" onClick={onClose} className="inline-flex items-center gap-2 text-[12px] text-blue-600 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to policies
          </button>
          <h1 className="text-[24px] font-extrabold tracking-[-0.5px] text-[#0B1220] dark:text-gray-100 mt-3 flex items-center gap-2.5">
            Policy editor
            <Badge>Draft · v{version}</Badge>
          </h1>
          <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-1.5">
            {hasCurrent
              ? 'The current published version stays available while you prepare this draft.'
              : 'Write a policy, or upload one you already have. Save it as a draft first.'}
          </p>
        </div>
        <Button variant="primary" type="submit" loading={saving}>Save draft</Button>
      </header>

      {error && <Notice tone="red" className="mb-5">{error}</Notice>}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-5 items-start">
        <Card className="p-6 min-w-0">
          <div className="mb-6">
            <FieldLabel required>Policy title</FieldLabel>
            <input
              className="w-full border-0 border-b border-[#E3E9F3] dark:border-gray-800 rounded-none bg-transparent px-0 pb-3 pt-2 text-[22px] font-bold tracking-[-0.4px] text-[#0B1220] dark:text-gray-100 placeholder-[#CBD5E1] focus:outline-none focus:border-blue-500"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              maxLength={160}
              required
              placeholder="e.g. Medication management"
            />
          </div>

          <div className="inline-flex gap-1 p-1 bg-[#F1F5FA] dark:bg-gray-800 rounded-[10px] mb-6">
            {[['text', 'Write policy', Edit3], ['upload', 'Upload document', Upload]].map(([value, label, Icon]) => (
              <button
                key={value}
                type="button"
                onClick={() => set('format', value)}
                aria-pressed={form.format === value}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-[12.5px] font-semibold transition-colors ${
                  form.format === value
                    ? 'bg-white dark:bg-[#262b33] text-[#0B1220] dark:text-gray-100 shadow-sm'
                    : 'text-[#64748B] dark:text-gray-400'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {form.format === 'text' ? (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap border-b border-[#E3E9F3] dark:border-gray-800 pb-3.5 mb-2">
                <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#94A3B8] dark:text-gray-500">
                  Policy content
                </span>
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<LayoutTemplate className="w-4 h-4" />}
                    aria-expanded={tool === 'template'}
                    onClick={() => setTool(tool === 'template' ? null : 'template')}
                  >
                    Use template
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Sparkles className="w-4 h-4" />}
                    aria-expanded={tool === 'patch'}
                    onClick={() => setTool(tool === 'patch' ? null : 'patch')}
                  >
                    Structure with Patch
                  </Button>
                </div>
              </div>

              {tool === 'template' && (
                <div className="border border-[#E3E9F3] dark:border-gray-800 rounded-xl p-4 my-4 bg-[#FAFCFF] dark:bg-gray-800/30">
                  <FieldLabel hint="Optional">Start from a template</FieldLabel>
                  <Select
                    value={templateKey}
                    onChange={setTemplateKey}
                    options={[
                      { value: '', label: 'Blank policy' },
                      ...Object.entries(templates || {}).map(([key, t]) => ({ value: key, label: t.name })),
                    ]}
                  />
                  <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-2 leading-relaxed">
                    Headings and questions only. Complete them with your service&apos;s agreed information before publishing.
                  </p>
                  {templateText && (
                    <>
                      <pre className="whitespace-pre-wrap font-sans text-[12px] leading-[1.7] bg-white dark:bg-[#1f242b] border border-[#E3E9F3] dark:border-gray-800 rounded-lg p-3 max-h-[220px] overflow-auto mt-3">
                        {templateText}
                      </pre>
                      <Button size="sm" variant="secondary" className="mt-3" onClick={insertTemplate}>
                        Insert template
                      </Button>
                      <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-2">
                        Existing text is kept — the template is added below anything you have already written.
                      </p>
                    </>
                  )}
                </div>
              )}

              {tool === 'patch' && (
                <div className="border border-[#DCE5FA] dark:border-blue-900/40 rounded-xl p-4 my-4 bg-[#F7FAFF] dark:bg-blue-900/10">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <strong className="text-[13px] text-[#0B1220] dark:text-gray-100">Structure with Patch</strong>
                      <p className="text-[11.5px] text-[#64748B] dark:text-gray-400 mt-1 max-w-md leading-relaxed">
                        Type naturally above. Patch organises your words — it cannot add facts, procedures,
                        contacts or timescales, and it is not a compliance check.
                      </p>
                    </div>
                    <Button size="sm" variant="secondary" onClick={runPatch} loading={patchRunning}>
                      Preview structure
                    </Button>
                  </div>

                  {patch && (
                    <div className="mt-4">
                      <FieldLabel>Structure preview</FieldLabel>
                      <textarea
                        readOnly
                        rows={10}
                        className={`${inputClass} bg-white dark:bg-[#1f242b]`}
                        value={patch.structured}
                      />

                      {patch.gaps?.length > 0 && (
                        <Notice tone="amber" className="mt-3">
                          <strong className="block mb-1.5">Patch could not find this information</strong>
                          <ul className="list-disc pl-5 space-y-1">
                            {patch.gaps.map((gap) => <li key={gap}>{gap}</li>)}
                          </ul>
                        </Notice>
                      )}

                      {patch.changes?.length > 0 && (
                        <Notice tone="gray" className="mt-2.5">
                          <strong className="block mb-1.5">Editorial changes</strong>
                          <ul className="list-disc pl-5 space-y-1">
                            {patch.changes.map((change) => <li key={change}>{change}</li>)}
                          </ul>
                        </Notice>
                      )}

                      <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-2.5 leading-relaxed">
                        Review note: read this against your own wording before using it. Patch has not checked
                        completeness, safety or any regulatory requirement.
                      </p>

                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="primary" onClick={applyPatch}>Use this structure</Button>
                        <Button size="sm" variant="secondary" onClick={() => setPatch(null)}>Dismiss</Button>
                      </div>
                    </div>
                  )}

                  {patchUndo !== null && !patch && (
                    <Button size="sm" variant="secondary" className="mt-3" leftIcon={<Undo2 className="w-4 h-4" />} onClick={undoPatch}>
                      Undo structure
                    </Button>
                  )}
                </div>
              )}

              <textarea
                ref={bodyRef}
                className="w-full border-0 bg-transparent px-0 py-4 text-[14px] leading-[1.9] text-[#334155] dark:text-gray-200 placeholder-[#CBD5E1] focus:outline-none resize-y min-h-[420px]"
                value={form.body}
                maxLength={MAX_BODY}
                onChange={(e) => set('body', e.target.value)}
                placeholder="Start writing or paste your notes here…"
                aria-label="Policy content"
              />
            </>
          ) : (
            <div>
              <FieldLabel required={!form.existingFile}>Policy document</FieldLabel>
              <label
                htmlFor="policy-document-input"
                className="flex flex-col items-center justify-center border-2 border-dashed border-[#D7E4FC] dark:border-gray-700 rounded-xl py-10 px-4 cursor-pointer hover:bg-[#F8FAFD] dark:hover:bg-gray-800/40"
              >
                <Upload className="w-6 h-6 text-blue-600 mb-2.5" />
                <strong className="text-[13.5px] text-[#0B1220] dark:text-gray-100 text-center break-all px-2">
                  {form.file ? form.file.name : 'Choose a PDF or Word document'}
                </strong>
                <span className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 mt-1.5 text-center">
                  {form.file
                    ? `${(form.file.size / 1024 / 1024).toFixed(2)} MB · ready to upload`
                    : 'PDF, DOC or DOCX · up to 10 MB. The original document is kept for viewing and download.'}
                </span>
                <input
                  id="policy-document-input"
                  type="file"
                  // Both the mimetypes and the extensions: browsers are
                  // inconsistent about what they attach to a .doc, and on some
                  // systems the mimetype alone hides the file from the picker.
                  accept="application/pdf,.pdf,application/msword,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
                  className="sr-only"
                  onChange={(e) => set('file', e.target.files?.[0] || null)}
                />
              </label>

              {form.existingFile && !form.file && (
                <Notice tone="blue" className="mt-3">
                  <span className="inline-flex items-center gap-2">
                    <FileText className="w-4 h-4 shrink-0" />
                    <span>
                      Current file: <strong>{form.existingFile.name}</strong>
                      {form.existingFile.label ? ` (${form.existingFile.label})` : ''}. Choose a file only if
                      you want to replace it.
                    </span>
                  </span>
                </Notice>
              )}

              <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-3 leading-relaxed">
                A Word document is stored and offered as a download — browsers cannot display one in
                the page. Upload a PDF if you want staff to be able to read the policy without
                downloading it.
              </p>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100">Policy details</h3>
          <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-1.5 mb-5 leading-relaxed">
            Keep ownership and review dates up to date.
          </p>

          <div className="space-y-4">
            <div>
              <FieldLabel>Category</FieldLabel>
              <Select value={form.category} onChange={(v) => set('category', v)} options={categories} />
              <button
                type="button"
                onClick={onManageCategories}
                className="text-[11px] text-blue-600 hover:underline mt-1.5"
              >
                Manage categories
              </button>
            </div>

            <div>
              <FieldLabel required hint="A role or a person">Policy owner</FieldLabel>
              <input
                className={inputClass}
                value={form.owner_name}
                onChange={(e) => set('owner_name', e.target.value)}
                maxLength={120}
                required
                placeholder="Who is responsible"
              />
            </div>

            <div>
              <FieldLabel required>Next review date</FieldLabel>
              <input
                type="date"
                className={inputClass}
                value={form.review_date}
                onChange={(e) => set('review_date', e.target.value)}
                required
              />
            </div>
          </div>

          <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 leading-[1.7] border-t border-[#E3E9F3] dark:border-gray-800 pt-4 mt-5">
            Only published policies are available as the current version. Your work stays a draft
            until it is reviewed and approved — publishing creates a numbered version with an
            effective date.
          </p>
        </Card>
      </div>
    </form>
  );
};

export default PolicyEditor;
